import React from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { browserHistory } from 'utils/navigation'
import {
  AutocompleteSearch,
  Button,
  Checkbox,
  DatePicker,
  Dropdown,
  Input,
  Loader,
  Radio,
  StickyHeader,
} from 'components/UIElements'
import { formatAndSanitizeHTML } from 'utils/richText'
import { stripHtmlTags } from 'utils/misc'
import { DCF_ACTION_OPTIONS } from '../modules/CreateDCF'
import { parseChoiceValueList } from '../../../utils/surveyQuestions'
import '../stylesheets/createdcf.scss'

class CreateDCFPage extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      paperFrom: '',
      paperTo: '',
      startFilterFrom: '',
      startFilterTo: '',
      hasSearched: false,
    }
  }

  componentDidMount() {
    const { resetCreateDcf, fetchParticipantsForDcf, fetchInstrumentsForDcf, params } = this.props
    const { studyID } = params
    resetCreateDcf()
    fetchParticipantsForDcf(studyID)
    fetchInstrumentsForDcf(studyID)
  }

  componentWillUnmount() {
    const { resetCreateDcf } = this.props
    resetCreateDcf()
  }

  needsInstance = () => {
    const { selectedAction } = this.props
    return selectedAction !== 'create_new_record'
  }

  needsVersion = () => {
    const { selectedAction } = this.props
    return selectedAction === 'create_new_record' || selectedAction === 'add_new_record'
  }

  isFlagOnly = () => {
    const { selectedAction } = this.props
    return selectedAction === 'flag_questions_only'
  }

  canCreateNewFromSearch = () => true

  isInstanceCompleted = candidate => {
    const status = String(candidate?.status || '').toLowerCase()
    if (status === 'completed') return true
    if (status === 'pending') return false
    return Boolean(candidate?.completedBy)
  }

  matchesStartDateFilter = (candidate, startFrom, startTo) => {
    if (!startFrom && !startTo) return true
    if (!candidate?.startedBy || !moment(candidate.startedBy).isValid()) return false
    const started = moment(candidate.startedBy)
    if (startFrom && started.isBefore(moment(startFrom), 'day')) return false
    if (startTo && started.isAfter(moment(startTo), 'day')) return false
    return true
  }

  onStartFilterChange = (field, value) => {
    const { selectedInstance, instances = [], setSelectedInstance } = this.props
    const { startFilterFrom, startFilterTo } = this.state
    const nextFrom = field === 'from' ? value : startFilterFrom
    const nextTo = field === 'to' ? value : startFilterTo
    this.setState({
      [field === 'from' ? 'startFilterFrom' : 'startFilterTo']: value,
    })
    if (selectedInstance?.id) {
      const candidate = instances.find(item => item.id === selectedInstance.id)
      if (!this.matchesStartDateFilter(candidate, nextFrom, nextTo) && setSelectedInstance) {
        setSelectedInstance(null)
      }
    }
  }

  resetInstanceSearch = () => {
    const { setInstances, setSelectedInstance } = this.props
    this.setState({
      hasSearched: false,
      startFilterFrom: '',
      startFilterTo: '',
    })
    if (setInstances) setInstances([])
    if (setSelectedInstance) setSelectedInstance(null)
  }

  onSelectAction = item => {
    const { setSelectedAction } = this.props
    setSelectedAction(item?.key || item)
    this.setState({ paperFrom: '', paperTo: '', hasSearched: false })
  }

  onSelectParticipant = item => {
    const { setSelectedParticipant, setSelectedInstrument } = this.props
    setSelectedParticipant(item)
    this.resetInstanceSearch()
    this.setState({ paperFrom: '', paperTo: '' })
    if (!item) {
      setSelectedInstrument(null)
    }
  }

  onSelectInstrument = item => {
    const { setSelectedInstrument, setInstrumentVersion } = this.props
    setSelectedInstrument(item)
    if (item?.version) setInstrumentVersion(String(item.version))
    this.resetInstanceSearch()
    this.setState({ paperFrom: '', paperTo: '' })
  }

  onPaperRangeChange = (field, value) => {
    this.setState({ [field]: value, hasSearched: false })
    const { setInstances, setSelectedInstance } = this.props
    if (setInstances) setInstances([])
    if (setSelectedInstance) setSelectedInstance(null)
  }

  toApiDateTime = value => {
    if (!value) return null
    // datetime-local is YYYY-MM-DDTHH:mm — FastAPI accepts this ISO form.
    return value.length === 16 ? `${value}:00` : value
  }

  canSearchInstances = () => {
    const { selectedParticipant, selectedInstrument } = this.props
    return Boolean(selectedParticipant?.key && selectedInstrument?.key)
  }

  onSearchInstances = () => {
    const { fetchInstances, params, selectedParticipant, selectedInstrument } = this.props
    if (!this.canSearchInstances()) return
    this.setState({ hasSearched: true })
    fetchInstances(params.studyID, selectedParticipant.key, selectedInstrument.key)
  }

  onSelectInstance = instanceId => {
    const { setSelectedInstance, setSelectedAction, instances, setDates } = this.props
    const candidate = (instances || []).find(item => item.id === instanceId)
    const payload = candidate?.payload || {}
    const completed = this.isInstanceCompleted(candidate)
    setSelectedAction(completed ? 'add_data_to_existing_record' : 'add_new_record')
    setSelectedInstance(candidate ? { id: instanceId, payload } : { id: instanceId })
    const started = candidate?.startedBy || payload.started_by || payload.start_date
    const completedAt = candidate?.completedBy || payload.completed_by || payload.completion
    setDates({
      startedBy: started ? moment(started).format('YYYY-MM-DD') : undefined,
      completedBy: completedAt ? moment(completedAt).format('YYYY-MM-DD') : undefined,
    })
  }

  onCreateNewInstance = () => {
    const { setSelectedAction, setSelectedInstance, setDates, selectedInstrument, setInstrumentVersion } =
      this.props
    const { paperFrom, paperTo } = this.state
    setSelectedAction('create_new_record')
    setSelectedInstance(null)
    if (selectedInstrument?.version) setInstrumentVersion(String(selectedInstrument.version))
    setDates({
      startedBy: paperFrom ? moment(paperFrom).format('YYYY-MM-DD') : undefined,
      completedBy: paperTo ? moment(paperTo).format('YYYY-MM-DD') : undefined,
    })
  }

  formatInstanceDateTime = value => {
    if (!value) return null
    const parsed = moment(value)
    return parsed.isValid() ? parsed.format('MMM D, YYYY h:mm A') : String(value)
  }

  onDateChange = (field, day) => {
    const { setDates } = this.props
    const value = day ? moment(day).format('YYYY-MM-DD') : ''
    setDates({ [field]: value })
  }

  onCancel = () => {
    const { params } = this.props
    const { studyID, studyVersionID } = params
    browserHistory.push(`/studies/${studyID}/${studyVersionID}/data-correction-v2`)
  }

  onNext = () => {
    const { setStep, fetchSurveyQuestions, params, selectedInstrument, selectedAction } = this.props

    if (selectedAction === 'flag_questions_only') {
      setStep(2)
      return
    }

    // Instance is associated later from the list; load blank survey rows from the instrument.
    fetchSurveyQuestions(params.studyID, selectedInstrument.key).then(result => {
      if (result !== false) setStep(2)
    })
  }

  onBack = () => {
    const { setStep } = this.props
    setStep(1)
  }

  onSaveDraft = () => {
    const { submitDcf, params } = this.props
    const { studyID, studyVersionID } = params
    submitDcf(studyID, studyVersionID, { asDraft: true })
  }

  onSubmit = () => {
    const { submitDcf, params } = this.props
    const { studyID, studyVersionID } = params
    submitDcf(studyID, studyVersionID, { asDraft: false })
  }

  canGoNext = () => {
    const {
      selectedParticipant,
      selectedInstrument,
      selectedAction,
      instrumentVersion,
      startedBy,
      completedBy,
    } = this.props

    if (!selectedParticipant?.key || !selectedInstrument?.key || !selectedAction) return false
    if (this.needsVersion() && !instrumentVersion) return false
    // Flag-only does not require dates; create/add/update do (used on apply).
    if (!this.isFlagOnly() && (!startedBy || !completedBy)) return false
    return true
  }

  renderSanitizedHtml = (html, className) => (
    <span
      className={className}
      dangerouslySetInnerHTML={{ __html: formatAndSanitizeHTML(html) }}
    />
  )

  getMultipleChoiceValues = value => parseChoiceValueList(value)

  selectChoice = (answer, choice) => {
    const { updateAnswer } = this.props
    updateAnswer(answer.questionId, choice.value, 'correctedAnswer', { answerId: choice.id })
  }

  toggleMultipleChoice = (answer, choice) => {
    const { updateAnswer } = this.props
    const selectedIds = this.getMultipleChoiceValues(answer.answerId)
    const selectedValues = this.getMultipleChoiceValues(answer.correctedAnswer)
    const isSelected = selectedIds.includes(choice.id) || selectedValues.includes(choice.value)
    const nextIds = isSelected
      ? selectedIds.filter(id => id !== choice.id)
      : [...selectedIds.filter(id => id !== choice.id), choice.id]
    const nextValues = isSelected
      ? selectedValues.filter(value => value !== choice.value)
      : [...selectedValues.filter(value => value !== choice.value), choice.value]
    updateAnswer(answer.questionId, JSON.stringify(nextValues), 'correctedAnswer', {
      answerId: JSON.stringify(nextIds),
    })
  }

  renderSurveyAnswerInput = answer => {
    const { updateAnswer } = this.props
    const { questionId, questionType, choices = [], inputValidation = {} } = answer
    const value = answer.correctedAnswer ?? ''
    const selectedAnswerId = answer.answerId != null ? String(answer.answerId) : ''
    const singleChoiceTypes = new Set([
      'select_one',
      'long_list_select_one',
      'likert',
      'numeric_rating_scale',
      'nrs',
      'horizontal_select_one',
    ])

    if (singleChoiceTypes.has(questionType)) {
      const isScale = ['likert', 'numeric_rating_scale', 'nrs', 'horizontal_select_one'].includes(
        questionType,
      )
      return (
        <div className={`survey-choice-list${isScale ? ' scale-choice-list' : ''}`}>
          {choices.map(choice => (
            <Radio
              key={choice.id}
              id={`dcf-answer-${questionId}-${choice.id}`}
              selected={
                selectedAnswerId === choice.id ||
                (!selectedAnswerId && String(value) === choice.value)
              }
              content={this.renderSanitizedHtml(choice.label, 'choice-label')}
              onClick={() => this.selectChoice(answer, choice)}
            />
          ))}
        </div>
      )
    }

    if (questionType === 'select_multiple') {
      const selectedIds = this.getMultipleChoiceValues(answer.answerId)
      const selectedValues = this.getMultipleChoiceValues(value)
      return (
        <div className='survey-choice-list'>
          {choices.map(choice => (
            <Checkbox
              key={choice.id}
              id={`dcf-answer-${questionId}-${choice.id}`}
              checked={selectedIds.includes(choice.id) || selectedValues.includes(choice.value)}
              label={this.renderSanitizedHtml(choice.label, 'choice-label')}
              onClick={() => this.toggleMultipleChoice(answer, choice)}
            />
          ))}
        </div>
      )
    }

    if (questionType === 'date' || questionType === 'datetime' || questionType === 'time') {
      let inputType = 'time'
      if (questionType === 'datetime') inputType = 'datetime-local'
      if (questionType === 'date') inputType = 'date'
      return (
        <Input
          id={`dcf-answer-${questionId}`}
          type={inputType}
          value={value}
          onChange={nextValue => updateAnswer(questionId, nextValue)}
        />
      )
    }

    const isInteger = questionType === 'integer'
    const isNumeric = isInteger || questionType === 'decimal'
    return (
      <Input
        id={`dcf-answer-${questionId}`}
        type={isNumeric ? 'number' : 'text'}
        onlyInteger={isInteger}
        min={inputValidation.min}
        max={inputValidation.max}
        step={questionType === 'decimal' ? 'any' : undefined}
        value={value}
        onChange={nextValue => updateAnswer(questionId, nextValue)}
        placeholder='Enter answer value'
      />
    )
  }

  /** Original answers are stored as raw values; show the choice label when we know it. */
  formatOriginalAnswer = answer => {
    const { choices = [], originalAnswer, originalAnswerId } = answer
    const raw = String(originalAnswer)
    if (!choices.length) return raw
    const selectedIds = parseChoiceValueList(originalAnswerId)
    const selectedValues = parseChoiceValueList(originalAnswer)
    const labels = choices
      .filter(choice => selectedIds.includes(choice.id) || selectedValues.includes(choice.value))
      .map(choice => stripHtmlTags(choice.label))
      .filter(Boolean)
    return labels.length ? labels.join(', ') : raw
  }

  renderQuestionLabel = answer => {
    if (!answer.isSurveyAnswer) return answer.questionTitle || answer.questionLabel
    return this.renderSanitizedHtml(
      answer.questionHtml || answer.questionTitle || answer.questionLabel,
      'question-label-content',
    )
  }

  renderInstancePicker = () => {
    const { instances = [], selectedInstance, selectedAction } = this.props
    const { paperFrom, paperTo, startFilterFrom, startFilterTo, hasSearched } = this.state
    const rangeInvalid = paperFrom && paperTo && paperFrom > paperTo
    const startFilterInvalid = Boolean(
      startFilterFrom && startFilterTo && startFilterFrom > startFilterTo,
    )
    const filteredInstances = instances.filter(candidate =>
      this.matchesStartDateFilter(candidate, startFilterFrom, startFilterTo),
    )
    const showPaperDates = selectedAction === 'create_new_record'

    return (
      <div className='form-section instances-section'>
        <label className='field-label'>Instrument instances</label>
        <p className='helper-text'>
          Load all database instances for this participant and instrument. Selecting a completed
          instance sets Add data to existing record; an incomplete instance sets Add new record.
        </p>
        <div className='date-fields flexed start-justified paper-range-fields'>
          <div className='date-field search-action'>
            <Button
              id='dcf-search-instances'
              content={hasSearched ? 'Reload instances' : 'Load instances'}
              disabled={!this.canSearchInstances()}
              onClick={this.onSearchInstances}
            />
          </div>
        </div>

        {hasSearched && (
          <div className='instance-candidates'>
            <label className='field-label'>Instances</label>
            {instances.length > 0 && (
              <div className='instance-start-filter'>
                <p className='helper-text'>Filter by start date to narrow a long list.</p>
                <div className='date-fields flexed start-justified paper-range-fields'>
                  <div className='date-field'>
                    <label className='field-label' htmlFor='dcf-start-from'>
                      Started from
                    </label>
                    <Input
                      id='dcf-start-from'
                      type='date'
                      value={startFilterFrom}
                      onChange={value => this.onStartFilterChange('from', value)}
                    />
                  </div>
                  <div className='date-field'>
                    <label className='field-label' htmlFor='dcf-start-to'>
                      Started to
                    </label>
                    <Input
                      id='dcf-start-to'
                      type='date'
                      value={startFilterTo}
                      onChange={value => this.onStartFilterChange('to', value)}
                    />
                  </div>
                </div>
                {startFilterInvalid && (
                  <div className='range-error'>
                    Started from must be earlier than or equal to Started to.
                  </div>
                )}
                {!startFilterInvalid && (startFilterFrom || startFilterTo) && (
                  <p className='filter-result-count'>
                    Showing {filteredInstances.length} of {instances.length} instances
                  </p>
                )}
              </div>
            )}
            {instances.length === 0 ? (
              <div className='no-instances'>
                No instances found for this participant and instrument.
              </div>
            ) : filteredInstances.length === 0 ? (
              <div className='no-instances'>No instances match this start date filter.</div>
            ) : (
              <ul className='instance-candidate-list'>
                {filteredInstances.map(candidate => {
                  const startedLabel = this.formatInstanceDateTime(candidate.startedBy)
                  const completedLabel = this.formatInstanceDateTime(candidate.completedBy)
                  const completed = this.isInstanceCompleted(candidate)
                  const isSelected = selectedInstance?.id === candidate.id
                  return (
                    <li key={candidate.id}>
                      <button
                        type='button'
                        className={`instance-candidate-card${isSelected ? ' selected' : ''}`}
                        onClick={() => this.onSelectInstance(candidate.id)}
                      >
                        <Radio justCircle selected={isSelected} onClick={() => {}} />
                        <div className='instance-candidate-meta'>
                          <div className='instance-candidate-line'>
                            <span className='meta-label'>Status</span>
                            <strong
                              className={`instance-status ${completed ? 'completed' : 'pending'}`}
                            >
                              {completed ? 'Completed' : 'Not completed'}
                            </strong>
                          </div>
                          <div className='instance-candidate-line'>
                            <span className='meta-label'>Started</span>
                            <strong>{startedLabel || '—'}</strong>
                          </div>
                          <div className='instance-candidate-line'>
                            <span className='meta-label'>Completed</span>
                            <strong>{completedLabel || '—'}</strong>
                          </div>
                          <div className='instance-candidate-line'>
                            <span className='meta-label'>Action</span>
                            <strong>
                              {completed ? 'Add data to existing record' : 'Add new record'}
                            </strong>
                          </div>
                        </div>
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}

            {this.canCreateNewFromSearch() && (
              <div className='create-new-instance-action'>
                <Button
                  grey
                  id='dcf-create-new-instance'
                  content='None of these — create a new instance'
                  onClick={this.onCreateNewInstance}
                />
              </div>
            )}

            {showPaperDates && (
              <>
                <label className='field-label'>Paper form date range</label>
                <p className='helper-text'>
                  Required for Create New Record. These dates are saved on the DCF and used when the
                  instance is created on Apply.
                </p>
                <div className='date-fields flexed start-justified paper-range-fields'>
                  <div className='date-field'>
                    <label className='field-label' htmlFor='dcf-paper-from'>
                      From
                    </label>
                    <Input
                      id='dcf-paper-from'
                      type='datetime-local'
                      value={paperFrom}
                      onChange={value => this.onPaperRangeChange('paperFrom', value)}
                    />
                  </div>
                  <div className='date-field'>
                    <label className='field-label' htmlFor='dcf-paper-to'>
                      To
                    </label>
                    <Input
                      id='dcf-paper-to'
                      type='datetime-local'
                      value={paperTo}
                      onChange={value => this.onPaperRangeChange('paperTo', value)}
                    />
                  </div>
                </div>
                {rangeInvalid && (
                  <div className='range-error'>From must be earlier than or equal to To.</div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    )
  }

  renderStepOne = () => {
    const {
      participants = [],
      instruments = [],
      selectedParticipant,
      selectedInstrument,
      selectedAction,
      instrumentVersion,
      setInstrumentVersion,
      startedBy,
      completedBy,
    } = this.props

    const selectedActionOption =
      DCF_ACTION_OPTIONS.find(opt => opt.key === selectedAction) || DCF_ACTION_OPTIONS[2]

    return (
      <div className='create-dcf-step'>
        <div className='form-section'>
          <label className='field-label'>DCF Action</label>
          <Dropdown
            id='dcf-action-dropdown'
            options={DCF_ACTION_OPTIONS}
            selected={selectedActionOption.key}
            onSelect={this.onSelectAction}
          />
        </div>

        <div className='form-section'>
          <label className='field-label'>Participant</label>
          <AutocompleteSearch
            id='dcf-participant-search'
            absolutePositionedList
            hideSearchIcon
            hideCheckbox
            shouldReturnItem
            isDropdown
            // Browser form history would otherwise cover the options list.
            autocomplete='off'
            placeholder='Search by participant name or ID'
            list={participants}
            checked={selectedParticipant || {}}
            initialValue={selectedParticipant?.text || ''}
            toggleItem={this.onSelectParticipant}
          />
        </div>

        <div className='form-section'>
          <label className='field-label'>Instrument</label>
          <AutocompleteSearch
            id='dcf-instrument-search'
            absolutePositionedList
            hideSearchIcon
            hideCheckbox
            shouldReturnItem
            isDropdown
            // Browser form history would otherwise cover the options list.
            autocomplete='off'
            placeholder='Search by instrument name'
            list={instruments}
            checked={selectedInstrument || {}}
            initialValue={selectedInstrument?.text || ''}
            toggleItem={this.onSelectInstrument}
            disabled={!selectedParticipant?.key}
          />
        </div>

        {this.needsVersion() && (
          <div className='form-section'>
            <label className='field-label'>Instrument Version</label>
            <Input
              id='dcf-instrument-version'
              value={instrumentVersion || ''}
              onChange={value => setInstrumentVersion(value)}
              placeholder='e.g. 1'
            />
          </div>
        )}

        {!this.isFlagOnly() && (
          <div className='form-section date-fields flexed start-justified'>
            <div className='date-field'>
              <label className='field-label'>Start By</label>
              <DatePicker
                id='dcf-started-by'
                date={startedBy ? moment(startedBy).toDate() : null}
                onDayChange={day => this.onDateChange('startedBy', day)}
                placeholder='MM/DD/YYYY'
              />
            </div>
            <div className='date-field'>
              <label className='field-label'>Completed By</label>
              <DatePicker
                id='dcf-completed-by'
                date={completedBy ? moment(completedBy).toDate() : null}
                onDayChange={day => this.onDateChange('completedBy', day)}
                placeholder='MM/DD/YYYY'
              />
            </div>
          </div>
        )}

        <p className='helper-text'>
          An instrument instance can be linked later from the DCF list via Associate instance.
        </p>

        <div className='button-list flexed end-justified'>
          <Button grey id='cancel-dcf' content='Cancel' onClick={this.onCancel} />
          <Button id='next-dcf' content='Next' disabled={!this.canGoNext()} onClick={this.onNext} />
        </div>
      </div>
    )
  }

  renderStepTwo = () => {
    const { answers = [], updateAnswer, addAnswerRow, removeAnswerRow, selectedAction } = this.props
    const isCreate = selectedAction === 'create_new_record'
    const isFlag = this.isFlagOnly()
    const isSurveyBacked = answers.some(answer => answer.isSurveyAnswer)
    let helperText = 'Update the submitted answers below as needed. Leave unchanged if no correction is required.'
    if (isFlag) {
      helperText =
        'After you create this DCF, associate an instance and set transfer_ignore and reporting_ignore there. Apply writes those values to instruments_instances.'
    } else if (isCreate) {
          helperText =
            'Add survey answer rows. These are written to the associated instance when the DCF is applied.'
        } else if (isSurveyBacked) {
      helperText = 'This instance is incomplete. Enter the original survey answers below.'
    }
    const hasAnswerFields = !isFlag && (answers.length > 0 || isCreate)

    return (
      <div className='create-dcf-step answers-step'>
        <h4>{isFlag ? 'Confirm Flag Request' : 'Review and Update Answers'}</h4>
        <p className='helper-text'>{helperText}</p>

        {isFlag && (
          <div className='no-answers'>No answer edits are required for Flag Questions Only.</div>
        )}
        {!isFlag && answers.length === 0 && !isCreate && (
          <div className='no-answers'>No submitted answers available for this instance.</div>
        )}
        {hasAnswerFields && (
          <div className='answers-list'>
            {answers.map(answer => (
              <div className='answer-row' key={answer.questionId}>
                {answer.isManual ? (
                  <div className='manual-answer-fields flexed start-justified'>
                    <div className='manual-field'>
                      <label className='field-label'>Question Id</label>
                      <Input
                        id={`dcf-qid-${answer.questionId}`}
                        value={answer.questionIdEditable ?? ''}
                        onChange={value => updateAnswer(answer.questionId, value, 'questionIdEditable')}
                        placeholder='question_id'
                      />
                    </div>
                    <div className='manual-field'>
                      <label className='field-label'>Question #</label>
                      <Input
                        id={`dcf-qnum-${answer.questionId}`}
                        value={answer.questionNumber ?? ''}
                        onChange={value => updateAnswer(answer.questionId, value, 'questionNumber')}
                        placeholder='1'
                      />
                    </div>
                    <div className='manual-field grow'>
                      <label className='field-label'>Question Title</label>
                      <Input
                        id={`dcf-qtitle-${answer.questionId}`}
                        value={answer.questionTitle ?? ''}
                        onChange={value => updateAnswer(answer.questionId, value, 'questionTitle')}
                        placeholder='Title'
                      />
                    </div>
                  </div>
                ) : (
                  <div className='field-label survey-question-label'>
                    {answer.questionNumber != null && <span>{answer.questionNumber}. </span>}
                    {this.renderQuestionLabel(answer)}
                    <span className='question-id'> ({answer.questionId})</span>
                  </div>
                )}
                {answer.isSurveyAnswer ? (
                  this.renderSurveyAnswerInput(answer)
                ) : (
                  <Input
                    id={`dcf-answer-${answer.questionId}`}
                    value={answer.correctedAnswer ?? ''}
                    onChange={value => updateAnswer(answer.questionId, value)}
                    placeholder='Enter answer value'
                  />
                )}
                {!answer.isManual &&
                  answer.originalAnswer !== undefined &&
                  answer.originalAnswer !== null &&
                  answer.originalAnswer !== '' && (
                    <div className='original-answer'>
                      Original: {this.formatOriginalAnswer(answer)}
                    </div>
                  )}
                {answer.isManual && (
                  <Button
                    link
                    content='Remove'
                    onClick={() => removeAnswerRow(answer.questionId)}
                  />
                )}
              </div>
            ))}
          </div>
        )}

        {!isSurveyBacked && (isCreate || selectedAction === 'add_new_record') && (
          <div className='add-answer-row'>
            <Button grey content='Add Answer Row' onClick={addAnswerRow} />
          </div>
        )}

        <div className='button-list flexed end-justified'>
          <Button grey id='back-dcf' content='Back' onClick={this.onBack} />
          <Button grey id='save-dcf-draft' content='Save Draft' onClick={this.onSaveDraft} />
          <Button id='submit-dcf' content='Submit' onClick={this.onSubmit} />
        </div>
      </div>
    )
  }

  render() {
    const { step, loading } = this.props

    return (
      <div className={`create-dcf page${loading ? ' is-loading' : ''}`}>
        {loading && (
          <div className='dcf-page-loader-overlay' aria-busy='true' aria-live='polite'>
            <Loader />
          </div>
        )}
        <StickyHeader offset={0}>
          <h3>New Data Correction Form</h3>
          <div className='step-indicator'>Step {step} of 2</div>
        </StickyHeader>
        {step === 1 ? this.renderStepOne() : this.renderStepTwo()}
      </div>
    )
  }
}

CreateDCFPage.propTypes = {
  params: PropTypes.shape({
    studyID: PropTypes.string,
    studyVersionID: PropTypes.string,
  }),
  loading: PropTypes.bool,
  step: PropTypes.number,
  participants: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      text: PropTypes.string,
    }),
  ),
  instruments: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      text: PropTypes.string,
    }),
  ),
  instances: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      startedBy: PropTypes.string,
      completedBy: PropTypes.string,
      status: PropTypes.string,
      payload: PropTypes.shape({
        started_by: PropTypes.string,
        completed_by: PropTypes.string,
        status: PropTypes.string,
        instance_id: PropTypes.string,
      }),
    }),
  ),
  answers: PropTypes.arrayOf(
    PropTypes.shape({
      questionId: PropTypes.string,
      questionType: PropTypes.string,
    }),
  ),
  selectedParticipant: PropTypes.shape({
    key: PropTypes.string,
    text: PropTypes.string,
  }),
  selectedInstrument: PropTypes.shape({
    key: PropTypes.string,
    text: PropTypes.string,
    version: PropTypes.string,
  }),
  selectedInstance: PropTypes.shape({
    id: PropTypes.string,
    payload: PropTypes.shape({
      completed_by: PropTypes.string,
    }),
  }),
  selectedAction: PropTypes.string,
  instrumentVersion: PropTypes.string,
  startedBy: PropTypes.string,
  completedBy: PropTypes.string,
  resetCreateDcf: PropTypes.func,
  fetchParticipantsForDcf: PropTypes.func,
  fetchInstrumentsForDcf: PropTypes.func,
  fetchInstances: PropTypes.func,
  fetchAnswers: PropTypes.func,
  fetchSurveyQuestions: PropTypes.func,
  setInstances: PropTypes.func,
  setSelectedParticipant: PropTypes.func,
  setSelectedInstrument: PropTypes.func,
  setSelectedInstance: PropTypes.func,
  setSelectedAction: PropTypes.func,
  setInstrumentVersion: PropTypes.func,
  setDates: PropTypes.func,
  setStep: PropTypes.func,
  updateAnswer: PropTypes.func,
  addAnswerRow: PropTypes.func,
  removeAnswerRow: PropTypes.func,
  submitDcf: PropTypes.func,
}

export default CreateDCFPage
